import {useCallback, useMemo, useState} from "react";
import {findNavigationMatch} from "../mobileNavigationUtils";
import {CmLinkableType} from "../../../../types";

type LinkWithChildren = CmLinkableType & { children: CmLinkableType[] };

function hasChildren(item: CmLinkableType | null | undefined): item is LinkWithChildren {
    return !!item && Array.isArray((item as any)?.children) && (item as any).children.length > 0;
}

interface UseMobileNavigationResult {
    navigationLevel: 0 | 1 | 2;
    activeCategory: CmLinkableType | null;
    activeSubcategory: CmLinkableType | null;
    navigateToFirstLevel: () => void;
    navigateToSecondLevel: (category: CmLinkableType) => void;
    navigateToThirdLevel: (subcategory: CmLinkableType) => void;
    handleMenuItemClick: (item: CmLinkableType, onClose: () => void) => void;
    isItemActive: (item: CmLinkableType, isEasyLangMode?: boolean) => boolean;
    itemsToRender: CmLinkableType[] | null;
    totalItems: number;
    resetFromPage: () => void;
}

export function useMobileNavigation(items: Array<CmLinkableType>, page: any): UseMobileNavigationResult {
    const [navigationLevel, setNavigationLevel] = useState<0 | 1 | 2>(0);
    const [activeCategory, setActiveCategory] = useState<CmLinkableType | null>(null);
    const [activeSubcategory, setActiveSubcategory] = useState<CmLinkableType | null>(null);


    const navigateToFirstLevel = useCallback(() => {
        setNavigationLevel(0);
        setActiveCategory(null);
        setActiveSubcategory(null);
    }, []);

    const navigateToSecondLevel = useCallback((category: CmLinkableType) => {
        setNavigationLevel(1);
        setActiveCategory(category);
        setActiveSubcategory(null);
    }, []);

    const navigateToThirdLevel = useCallback((subcategory: CmLinkableType) => {
        setNavigationLevel(2);
        setActiveSubcategory(subcategory);
    }, []);

    const handleMenuItemClick = (item: CmLinkableType, onClose: () => void) => {
        if (navigationLevel === 0 && hasChildren(item)) {
            navigateToSecondLevel(item);
        } else if (navigationLevel === 1 && hasChildren(item)) {
            navigateToThirdLevel(item);
        } else {
            onClose();
        }
    };

    const resetFromPage = useCallback(() => {
        const match = findNavigationMatch(items, page?.selfUrl);
        if (match) {
            if (match.level === 0 && hasChildren(match.category)) {
                // If we're on a first level page and it has children, show its second level menu
                navigateToSecondLevel(match.category);
            } else if (match.level === 1) {
                // If we're on a second level page and that subcategory has children, show its third level menu
                if (match.subcategory) {
                    if (hasChildren(match.subcategory)) {
                        setActiveCategory(match.category);
                        navigateToThirdLevel(match.subcategory);
                    } else {
                        navigateToSecondLevel(match.category);
                    }
                } else {
                    navigateToSecondLevel(match.category);
                }
            } else if (match.level === 2) {
                setActiveCategory(match.category);
                if (match.subcategory) {
                    navigateToThirdLevel(match.subcategory);
                }
            }
        } else {
            // No match: default to first level
            navigateToFirstLevel();
        }
    }, [items, page, navigateToSecondLevel, navigateToThirdLevel, navigateToFirstLevel]);

    const isItemActive = (item: CmLinkableType, isEasyLangMode?: boolean) => {
        const itemHasVisibleChildren = navigationLevel < 2 && hasChildren(item);
        const current = (page?.selfUrl ?? '').replace(/\/+$/, '');
        const target = (item?.selfUrl ?? '').replace(/\/+$/, '');
        if (!target) {
            return false;
        }

        // Do not highlight item if it has visible children
        if (itemHasVisibleChildren) {
            return false;
        }

        // Highlight item if it exactly matches the current page
        if (current === target) {
            return true;
        }

        // Highlight item if the current page is a descendant of the item's URL (deep page without its own nav item)
        return !isEasyLangMode && current.startsWith(target + '/');
    };

    const itemsToRender = useMemo(() => {
        if (navigationLevel === 0) return items;
        if (navigationLevel === 1) return hasChildren(activeCategory) ? activeCategory.children : null;
        if (navigationLevel === 2) return hasChildren(activeSubcategory) ? activeSubcategory.children : null;
        return null;
    }, [navigationLevel, items, activeCategory, activeSubcategory]);

    const totalItems = itemsToRender?.length ?? 0;

    return {
        navigationLevel,
        activeCategory,
        activeSubcategory,
        navigateToFirstLevel,
        navigateToSecondLevel,
        navigateToThirdLevel,
        handleMenuItemClick,
        isItemActive,
        itemsToRender,
        totalItems,
        resetFromPage
    };
}